import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgForm, NgModel } from '@angular/forms';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  EMAIL_REGEX,
  passwordRegex,
  passwordValidationMessage,
} from '../../../../core/regex/validators-regex';
import { AuthService } from '../../../../core/services/auth.service';
import { LanguageService } from '../../../../core/services/language.service';
import { ErrorModalComponent } from '../../../../shared/components/error-modal/error-modal.component';
import { FieldsetDirective } from '../../../../shared/directives/fieldset.directive';
import { ComponentBase } from '../../../../shared/models/component-base';
import { HttpException } from '../../../../shared/models/http-exception';
import {
  GoogleAnalyticsService,
  GoogleAnalyticsCategories,
} from '../../../../shared/services/google-analytics.service';
import { Agency } from '../../models/agency';
import { SignupRequest } from '../../models/signup-request';
import { SignupService } from '../../services/signup.service';
import { AppConfigService } from '../../../../core/services/app-config.service';

export const PASSWORD_STORAGE_KEY = 'account';

/** Steps to follow in the registration process */
enum Steps {
  Account = 1,
  Signup = 2,
  AwaitConfirmation = 3,
}

/** Allows the user to register in the application */
@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignupComponent extends ComponentBase implements AfterViewInit {
  EMAIL_REGEX = EMAIL_REGEX;
  /** Saves the agencies */
  agencies$ = new BehaviorSubject<Array<Agency>>([]);
  /** Saves the steps */
  steps = Steps;
  /** Saves the current step */
  activeStep: Steps = Steps.Account;
  /** Province control */
  @ViewChild('agencyControl', { static: true }) agencyControl: NgModel;
  /** Code control */
  @ViewChild('codeControl', { static: true }) codeControl: NgModel;
  /** Phone control */
  @ViewChild('phoneControl', { static: true }) phoneControl: NgModel;
  /** Request for the signup */
  request = new SignupRequest();
  /** Common fieldset */
  @ViewChild('commonFieldSet', { static: true })
  commonFieldSet: FieldsetDirective;
  /** Saves the email confirmation */
  confirmEMail: string;
  /**  Saves the password confirmation */
  passwordConfirmation: string;
  /** TemplateRef for the modal that indicates that the account does not exist */
  @ViewChild('doesNotExistModal', { static: true })
  doesNotExistModal: TemplateRef<any>;
  /** NIF modal */
  @ViewChild('nifModal', { static: true }) nifModal: TemplateRef<any>;
  /**  Review email modal */
  @ViewChild('reviewEmailModal', { static: true })
  reviewEmailModal: TemplateRef<any>;
  /** Indicates if the account is linked */
  linked?: boolean;
  /** Indicates if there is a pending request for a non-linked account */
  pendingRequestNonLinkedAccount = false;
  /** Reference to the component inside the modal */
  modalRef: BsModalRef;
  /** Indicates if the user accepts sending to the administrator */
  acceptsAdminSending = false;
  /** Indicates if password is remembered */
  rememberPassword = false;
  enterClicked = false;
  /** Almacena el formulario */
  @ViewChild('frm', { static: true }) frm: NgForm;
  invalidPhoneLength: boolean = false;

  private changeAccountSubscription: Subscription;
  /** Inicializa servicios y detección de cambios */
  constructor(
    private cdr: ChangeDetectorRef,
    private signupService: SignupService,
    private modalService: BsModalService,
    private authService: AuthService,
    private gaService: GoogleAnalyticsService,
    private langService: LanguageService,
    private configService: AppConfigService
  ) {
    super();
    this.addSubscription(
      signupService.getAgencies().subscribe((r) => this.agencies$.next(r))
    );

    const account = sessionStorage.getItem(PASSWORD_STORAGE_KEY);
    if (account) {
      try {
        this.request.Account.Code = JSON.parse(account).code;
        this.request.Account.Agency = JSON.parse(account).name;
      } catch (err) {}
    }
  }
  ngAfterViewInit() {
    this.subscribeAccountChange();
    this.subscribePhoneChange();
  }

  get passwordValidationMessage() {
    const policy = this.configService.appConfiguration?.passwordsPolicy;
    return passwordValidationMessage(
      policy.validationMessage[this.langService.getCurrentLanguage()],
      policy.requiredLength
    );
  }
  get passwordRegex() {
    const policy = this.configService.appConfiguration?.passwordsPolicy;
    return passwordRegex(policy.validationRegex, policy.requiredLength);
  }
  /** Obtains the text for the current step */
  get currentStepText(): string {
    return this.langService.instant(
      `REGISTRATION.TXTPROGRESO${this.activeStep}`
    );
  }

  private subscribeAccountChange() {
    this.changeAccountSubscription = merge(
      this.codeControl.valueChanges.pipe(distinctUntilChanged()),
      this.agencyControl.valueChanges.pipe(distinctUntilChanged())
    )
      .pipe(debounceTime(1000))
      .subscribe(() => {
        this.onChange();
      });
  }

  private subscribePhoneChange() {
    this.phoneControl.valueChanges
      .pipe(debounceTime(1000))
      .subscribe((value) => {
        if (value?.length < 9) {
          this.invalidPhoneLength = true;
          this.phoneControl.control.setErrors({ invalid: true });
        }
        else if(value === undefined){
          this.invalidPhoneLength = false;
          this.phoneControl.control.setErrors({ invalid: true });
        }
        else {
          this.invalidPhoneLength = false;
          this.phoneControl.control.setErrors(null);
        }

        this.cdr.detectChanges();
      });
  }

  private selectedAgency;
  isNaN = isNaN;
  Number = Number;

  private onChange() {
    if (!this.codeControl.value) {
      return;
    }

    const code = this.codeControl.value
      ? (<string>this.codeControl.value).trim().replace('-', '')
      : '';

    this.selectedAgency = this.agencies$.value.find(
      (p) => p.Code === this.request.Account.Agency
    );

    if (code.length > 7) {
      let agency = code.substring(0, code.length - 6);
      this.selectedAgency = this.agencies$.value.find((p) => p.Code === agency);
      this.request.Account.Code = code.substring(agency.length);
      if (agency !== this.request.Account.Agency) {
        if (!this.selectedAgency) {
          agency = '';
        }
        this.request.Account.Agency = agency;
        this.cdr.detectChanges();
        return;
      }
    }

    if (
      this.selectedAgency &&
      this.codeControl.value &&
      !isNaN(Number(this.codeControl.value))
    ) {
      this.request.Account.Code = this.agencyControl.disabled
        ? this.codeControl.value.substring(this.agencyControl.value.length)
        : this.codeControl.value;

      this.request.Account.Code = this.codeControl.value;
      this.request.Account.Agency = this.agencyControl.value;

      // This session storage is used to remember the agency and code when refreshed
      const account = sessionStorage.getItem(PASSWORD_STORAGE_KEY);
      if (!account) {
        sessionStorage.setItem(
          PASSWORD_STORAGE_KEY,
          JSON.stringify({
            code: this.request.Account.Code,
            name: this.request.Account.Agency,
          })
        );
      }
      if (
        this.request.Account.Code.length > 6 ||
        !this.request.Account.Agency
      ) {
        this.cdr.detectChanges();
        return;
      }

      this.authService
        .loginSignup(
          this.request.Account.Agency,
          this.request.Account.Code.padStart(6, '0')
        )
        .subscribe(
          (result) => {
            if (result) {
              this.linked = result.CuentaVinculada || result.NIFVinculado;
              this.activeStep = Steps.Signup;
            } else {
              this.modalRef = this.modalService.show(this.doesNotExistModal);
            }
            this.cdr.detectChanges();
          },

          (error) => {
            ErrorModalComponent.show(error);
            this.cdr.detectChanges();
          }
        );
    } else {
      this.cdr.detectChanges();
    }
  }

  /** Handles the form submission */
  onSubmit() {
    if (
      this.linked &&
      this.pendingRequestNonLinkedAccount &&
      !this.commonFieldSet.validate()
    ) {
      return;
    }
    if (this.linked) {
      this.frm.form.updateValueAndValidity();
      if (this.frm.valid) {
        this.requestSignup();
      }
    } else if (this.frm.valid) {
      this.modalRef = this.modalService.show(this.nifModal);
    }
  }

  /** Method for handling the click on the enter button */
  onEnterClick() {
    if (!this.request.AcceptsPrivacyPolicy) {
      this.enterClicked = true;
    }
    if (!this.linked || (this.linked && !this.commonFieldSet.validate())) {
      return;
    }

    this.pendingRequestNonLinkedAccount = true;
    this.cdr.detectChanges();
  }

  keydownCode(value: string) {
    this.codeControl.control.setValue(value, { emitEvent: true });
  }
  get progressText(): string {
    if (this.activeStep === this.steps.Account || !this.linked) {
      return this.langService.instant('REGISTRATION.PROGRESS_TXT3');
    }

    const text: string = this.langService.instant(
      'REGISTRATION.STEP3HEADER_TEXT'
    );
    return text.substring(text.indexOf(' ') + 1);
  }

  /** Request the signup to the API */
  requestSignup() {
    this.signupService.postSignupRequest(this.request).subscribe(
      (valid) => {
        if (valid) {
          if (!this.linked) {
            this.modalRef.hide();
            this.modalRef = this.modalService.show(this.reviewEmailModal);
          }
          this.activeStep = Steps.AwaitConfirmation;
          this.authService.logout(false);
          this.cdr.detectChanges();
          this.gaService.sendGoogleAnalyticsEvent(
            GoogleAnalyticsCategories.Otros,
            'Auto Registro OK'
          );
        } else {
          if (this.linked) {
            ErrorModalComponent.show(
              this.langService.instant('SHARED.ERROR_GENERIC')
            );
          } else {
            ErrorModalComponent.show(
              this.langService.instant('REGISTRATION.INVALID_NIF')
            );
          }
        }
      },
      (err) => {
        if (err instanceof HttpException && err.errors && err.errors[0]) {
          ErrorModalComponent.show(
            this.langService.instant(`SHARED.${err.errors[0]}`)
          );
        } else {
          ErrorModalComponent.show(err, this.frm.form);
          if (this.linked) {
            if (!this.frm.valid) {
              for (const controlName in this.frm.controls) {
                if (this.frm.controls.hasOwnProperty(controlName)) {
                  if (controlName !== 'request.PhoneNumber') {
                    this.pendingRequestNonLinkedAccount = false;
                    this.cdr.detectChanges();
                    break;
                  }
                }
              }
            }
          }
        }
      }
    );
  }
  /** Register the Google Analytics event */
  gaClickFAQ() {
    this.gaService.sendGoogleAnalyticsEvent(
      GoogleAnalyticsCategories.Otros,
      'FAQ'
    );
  }
}
